import React from 'react';
import { Calendar } from 'lucide-react';

// Financial year helpers
function getFY() {
  const now = new Date();
  const m = now.getMonth(); // 0-indexed, March = 2, April = 3
  const y = now.getFullYear();
  const fyStartYear = m >= 3 ? y : y - 1;
  return {
    from: `${fyStartYear}-04-01`,
    to:   `${fyStartYear + 1}-03-31`,
    label: `FY ${fyStartYear}/${String(fyStartYear + 1).slice(2)}`
  };
}

function getPreset(key) {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const today = fmt(now);

  if (key === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: fmt(start), to: today };
  }
  if (key === '3months') {
    const start = new Date(now); start.setMonth(start.getMonth() - 3);
    return { from: fmt(start), to: today };
  }
  if (key === 'year') {
    return { from: `${now.getFullYear()}-01-01`, to: today };
  }
  if (key === 'fy') {
    const fy = getFY();
    return { from: fy.from, to: fy.to };
  }
  return { from: '', to: '' };
}

const PRESETS = [
  { key: 'month',   label: 'This Month' },
  { key: '3months', label: 'Last 3 Months' },
  { key: 'year',    label: 'This Year' },
  { key: 'fy',      label: `This FY (${getFY().label})` },
];

/**
 * Reusable date range picker with quick preset buttons.
 * Props:
 *   from, to           — controlled date strings (YYYY-MM-DD)
 *   onChange(from, to) — called on any change
 *   compact            — smaller layout (default false)
 */
export default function DateRangePicker({ from, to, onChange, compact = false }) {
  const active = from || to;

  const applyPreset = key => {
    const p = getPreset(key);
    onChange(p.from, p.to);
  };

  const isPresetActive = key => {
    const p = getPreset(key);
    return from === p.from && to === p.to;
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${compact ? 'text-xs' : ''}`}>
      {/* Quick preset buttons */}
      {PRESETS.map(p => (
        <button
          key={p.key}
          onClick={() => isPresetActive(p.key) ? onChange('','') : applyPreset(p.key)}
          className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
          style={{
            background: isPresetActive(p.key) ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.04)',
            color: isPresetActive(p.key) ? '#60a5fa' : '#475569',
            border: isPresetActive(p.key) ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(255,255,255,0.07)',
          }}>
          {p.label}
        </button>
      ))}

      {/* Separator */}
      <span style={{ color: '#1e3a5f', fontSize: 10 }}>|</span>

      {/* Custom date range */}
      <div className="flex items-center gap-1.5">
        <Calendar size={11} style={{ color: '#475569', flexShrink: 0 }}/>
        <input
          type="date"
          value={from}
          onChange={e => onChange(e.target.value, to)}
          className="input-dark text-xs py-1"
          style={{ width: 128 }}
          title="From date"
        />
        <span style={{ color: '#334155', fontSize: 11 }}>→</span>
        <input
          type="date"
          value={to}
          onChange={e => onChange(from, e.target.value)}
          className="input-dark text-xs py-1"
          style={{ width: 128 }}
          title="To date"
        />
        {active && (
          <button
            onClick={() => onChange('', '')}
            className="text-xs px-2 py-1 rounded"
            style={{ color: '#f87171', background: 'rgba(248,113,113,0.1)' }}>
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
